<template>
  <div class="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div class="glass-strong rounded-xl p-6 w-full max-w-lg mx-4">
      <h3 class="text-xl font-bold text-white mb-2">Schedule Maintenance</h3>
      <p class="text-slate-400 text-sm mb-4">
        {{ make }} • {{ model }} — <span class="font-mono">{{ serial }}</span>
      </p>
      <div class="grid grid-cols-2 gap-3 text-sm">
        <select
          v-model="reason"
          class="px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white col-span-2"
        >
          <option value="" disabled>Select reason…</option>
          <option value="routine">Routine Maintenance</option>
          <option value="repair">Repair</option>
          <option value="inspection">Inspection</option>
          <option value="firmware">Firmware / Software Update</option>
          <option value="calibration">Calibration</option>
          <option value="other">Other</option>
        </select>
        <div class="col-span-2">
          <textarea
            v-model="notes"
            placeholder="Notes (optional)"
            rows="3"
            class="w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white"
          />
        </div>
        <label class="text-slate-300 text-xs">
          Start
          <input
            v-model="start"
            type="datetime-local"
            class="mt-1 w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white"
          />
        </label>
        <label class="text-slate-300 text-xs">
          Expected End
          <input
            v-model="end"
            type="datetime-local"
            class="mt-1 w-full px-3 py-2 bg-slate-800/70 border border-slate-700 rounded text-white"
          />
        </label>
      </div>
      <div class="flex gap-3 mt-4">
        <button
          class="px-3 py-2 rounded bg-slate-800 border border-slate-700 text-white"
          @click="$emit('close')"
        >
          Cancel
        </button>
        <button class="px-3 py-2 rounded bg-yellow-600 text-white hover:bg-yellow-500" @click="save">
          Submit
        </button>
      </div>
      <div v-if="message" class="text-slate-300 text-xs mt-2">{{ message }}</div>
      <div v-if="error" class="text-red-400 text-xs mt-2">{{ error }}</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { supabase } from '@/lib/supabaseClient'
import { getCurrentUserId } from '@/lib/appApi'

const props = defineProps<{ make: string; model: string; serial: string }>()
const emit = defineEmits(['saved', 'close'])

const reason = ref('')
const notes = ref('')
const start = ref('')
const end = ref('')
const message = ref('')
const error = ref('')

/** Default window: now → 3 days from now. */
onMounted(() => {
  const s = new Date()
  const e = new Date(s)
  e.setDate(e.getDate() + 3)
  start.value = new Date(s.getTime() - s.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  end.value = new Date(e.getTime() - e.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
})

/**
 * Creates a reservation row with purpose='maintenance'.
 * The reservation trigger detects this purpose and sets
 * equipment_serials.status to 'maintenance' instead of 'reserved'.
 */
async function save() {
  try {
    message.value = ''
    error.value = ''
    if (!reason.value) { error.value = 'Please select a reason.'; return }

    const userId = await getCurrentUserId()

    // Look up equipment serial
    const { data: serialRow } = await supabase
      .from('equipment_serials_v')
      .select('id')
      .eq('serial', props.serial)
      .single()
    if (!serialRow?.id) throw new Error('Equipment not found for serial: ' + props.serial)

    const payload: any = {
      equipment_serial_id: serialRow.id,
      created_by: userId,
      sales_engineer_id: userId,
      purpose: 'maintenance',
      start_at: new Date(start.value).toISOString(),
      end_at: new Date(end.value).toISOString(),
      notes: [reason.value, notes.value].filter(Boolean).join(' — '),
    }
    const { error: err } = await supabase.schema('app' as any).from('reservations').insert(payload)
    if (err) throw err
    message.value = 'Maintenance scheduled.'
    emit('saved')
  } catch (e: any) {
    error.value = e?.message || String(e)
  }
}
</script>

<style scoped></style>
